import { useState, useEffect, useCallback, useRef } from "react";
import { V } from "../theme.js";
import { OptBtn, AdjRow, Modal } from "../ui/components.jsx";
import { ANCHORS } from "../engine/constants.js";

// ── Debounced preview helper (1.5) ──
function useFilterPreview(open, filterType, params, onPreview) {
  const timerRef = useRef(null);
  const paramsStrRef = useRef(null); // tracks the serialized params to detect actual changes
  useEffect(() => {
    if (!open || !onPreview) return;
    const ps = JSON.stringify(params);
    // On first render after open, just record the initial params — don't fire preview
    if (paramsStrRef.current === null) { paramsStrRef.current = ps; return; }
    // Only fire if params actually changed from last seen value
    if (ps === paramsStrRef.current) return;
    paramsStrRef.current = ps;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onPreview(filterType, params), 50);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  });
  // Reset tracking when dialog closes
  useEffect(() => { if (!open) paramsStrRef.current = null; }, [open]);
}

// ── Levels ──────────────────────────────────────────────────────────────────

const DEFAULT_LEVELS = { inBlack:0, inWhite:255, gamma:1.0, outBlack:0, outWhite:255 };

export function LevelsDialog({ open, onCommit, onClose, onPreviewBegin, onPreview, onPreviewCancel }) {
  const [lvl, setLvl] = useState(DEFAULT_LEVELS);
  const beganRef = useRef(false);

  useEffect(() => {
    if (open && onPreviewBegin && !beganRef.current) { onPreviewBegin(); beganRef.current = true; }
    if (!open) beganRef.current = false;
  }, [open]);
  useFilterPreview(open, "levels", lvl, onPreview);

  if (!open) return null;
  return (
    <Modal onClose={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>
      <div style={{ padding:"4px 0" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:4 }}>Levels</div>
        <div style={{ fontSize:9, color:V.ghost, marginBottom:12 }}>Live preview</div>
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:10, color:V.muted, marginBottom:4 }}>Input Levels</div>
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
            <div style={{ flex:1 }}><AdjRow label="Black" value={lvl.inBlack} min={0}   max={254} onChange={v => setLvl(s => ({ ...s, inBlack:v }))}/></div>
            <div style={{ flex:1 }}><AdjRow label="White" value={lvl.inWhite} min={1}   max={255} onChange={v => setLvl(s => ({ ...s, inWhite:v }))}/></div>
          </div>
          <AdjRow label="Gamma" value={Math.round(lvl.gamma*100)} min={10} max={300}
            onChange={v => setLvl(s => ({ ...s, gamma:v/100 }))} fmt={v => (v/100).toFixed(2)}/>
        </div>
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:10, color:V.muted, marginBottom:4 }}>Output Levels</div>
          <div style={{ display:"flex", gap:8 }}>
            <div style={{ flex:1 }}><AdjRow label="Black" value={lvl.outBlack} min={0} max={254} onChange={v => setLvl(s => ({ ...s, outBlack:v }))}/></div>
            <div style={{ flex:1 }}><AdjRow label="White" value={lvl.outWhite} min={1} max={255} onChange={v => setLvl(s => ({ ...s, outWhite:v }))}/></div>
          </div>
        </div>
        <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
          <OptBtn onClick={() => setLvl(DEFAULT_LEVELS)}>Reset</OptBtn>
          <OptBtn onClick={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>Cancel</OptBtn>
          <OptBtn accent onClick={() => { onCommit(lvl); onClose(); }}>Apply</OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── Hue / Saturation ────────────────────────────────────────────────────────

const RANGE_COLORS = { master:V.muted, reds:"#f87171", yellows:"#facc15", greens:"#4ade80", cyans:"#22d3ee", blues:"#60a5fa", magentas:"#c084fc" };
const RANGES = ["master","reds","yellows","greens","cyans","blues","magentas"];
const DEFAULT_HS = { hue:0, saturation:0, lightness:0, range:"master" };

export function HueSatDialog({ open, onCommit, onClose, onPreviewBegin, onPreview, onPreviewCancel }) {
  const [p, setP] = useState(DEFAULT_HS);
  const beganRef = useRef(false);

  useEffect(() => {
    if (open && onPreviewBegin && !beganRef.current) { onPreviewBegin(); beganRef.current = true; }
    if (!open) { beganRef.current = false; setP(DEFAULT_HS); }
  }, [open]);
  useFilterPreview(open, "huesat", p, onPreview);

  if (!open) return null;
  return (
    <Modal onClose={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>
      <div style={{ padding:"4px 0" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:4 }}>Hue / Saturation</div>
        <div style={{ fontSize:9, color:V.ghost, marginBottom:8 }}>Live preview — applies to active layer</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginBottom:10 }}>
          {RANGES.map(r => {
            const col = RANGE_COLORS[r];
            return (
              <div key={r} onClick={() => setP(s => ({ ...s, range:r }))}
                style={{ padding:"3px 8px", fontSize:9, borderRadius:3, cursor:"pointer", fontFamily:V.mono, textTransform:"capitalize",
                  background: p.range===r ? `${col}18` : V.panelActive,
                  border:    `1px solid ${p.range===r ? col : V.panelBorder2}`,
                  color:      p.range===r ? col : V.muted, transition:"all 0.12s" }}>
                {r}
              </div>
            );
          })}
        </div>
        <AdjRow label="Hue"        value={p.hue}        min={-180} max={180} onChange={v => setP(s => ({ ...s, hue:v }))}        fmt={v => v+"°"}/>
        <AdjRow label="Saturation" value={p.saturation} min={-100} max={100} onChange={v => setP(s => ({ ...s, saturation:v }))}/>
        <AdjRow label="Lightness"  value={p.lightness}  min={-100} max={100} onChange={v => setP(s => ({ ...s, lightness:v }))}/>
        <div style={{ display:"flex", gap:6, justifyContent:"flex-end", marginTop:12 }}>
          <OptBtn onClick={() => setP(s => ({ ...s, hue:0, saturation:0, lightness:0 }))}>Reset</OptBtn>
          <OptBtn onClick={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>Cancel</OptBtn>
          <OptBtn accent onClick={() => { onCommit(p); onClose(); }}>Apply</OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── Gaussian Blur ────────────────────────────────────────────────────────────

export function GaussBlurDialog({ open, onCommit, onClose, onPreviewBegin, onPreview, onPreviewCancel }) {
  const [radius, setRadius] = useState(5);
  const beganRef = useRef(false);

  useEffect(() => {
    if (open && onPreviewBegin && !beganRef.current) { onPreviewBegin(); beganRef.current = true; }
    if (!open) beganRef.current = false;
  }, [open]);
  useFilterPreview(open, "gaussblur", { radius }, onPreview);

  if (!open) return null;
  return (
    <Modal onClose={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>
      <div style={{ padding:"4px 0" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:4 }}>Gaussian Blur</div>
        <div style={{ fontSize:9, color:V.ghost, marginBottom:8 }}>Live preview — applies to active layer</div>
        <AdjRow label="Radius" value={radius} min={1} max={100} onChange={setRadius} fmt={v => v+"px"}/>
        <div style={{ fontSize:9, color:V.ghost, marginTop:4, marginBottom:12 }}>
          σ ≈ {(radius/3).toFixed(1)} · Kernel: {radius*2+1}px
        </div>
        <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
          <OptBtn onClick={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>Cancel</OptBtn>
          <OptBtn accent onClick={() => { onCommit(radius); onClose(); }}>Apply</OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── Color Balance ────────────────────────────────────────────────────────────

const DEFAULT_CB = { shadowR:0,shadowG:0,shadowB:0, midR:0,midG:0,midB:0, highR:0,highG:0,highB:0, preserveLum:true };
const CB_ZONES = [
  { label:"Shadows",    prefix:"shadow", color:"#60a5fa" },
  { label:"Midtones",   prefix:"mid",    color:"#4ade80" },
  { label:"Highlights", prefix:"high",   color:"#fbbf24" },
];

export function ColorBalDialog({ open, onCommit, onClose, onPreviewBegin, onPreview, onPreviewCancel }) {
  const [p, setP] = useState(DEFAULT_CB);
  const beganRef = useRef(false);

  useEffect(() => {
    if (open && onPreviewBegin && !beganRef.current) { onPreviewBegin(); beganRef.current = true; }
    if (!open) beganRef.current = false;
  }, [open]);
  useFilterPreview(open, "colorbal", p, onPreview);
  const set = useCallback((k, v) => setP(s => ({ ...s, [k]:v })), []);

  if (!open) return null;
  return (
    <Modal onClose={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>
      <div style={{ padding:"4px 0" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:4 }}>Color Balance</div>
        <div style={{ fontSize:9, color:V.ghost, marginBottom:10 }}>Live preview — per-channel adjustment</div>
        {CB_ZONES.map(zone => (
          <div key={zone.prefix} style={{ marginBottom:10 }}>
            <div style={{ fontSize:10, fontWeight:600, color:zone.color, marginBottom:4, letterSpacing:"0.5px" }}>{zone.label}</div>
            <AdjRow label="Cyan ↔ Red"       value={p[zone.prefix+"R"]} min={-100} max={100} onChange={v => set(zone.prefix+"R", v)}/>
            <AdjRow label="Magenta ↔ Green"  value={p[zone.prefix+"G"]} min={-100} max={100} onChange={v => set(zone.prefix+"G", v)}/>
            <AdjRow label="Yellow ↔ Blue"    value={p[zone.prefix+"B"]} min={-100} max={100} onChange={v => set(zone.prefix+"B", v)}/>
          </div>
        ))}
        <label style={{ fontSize:10, color:V.muted, cursor:"pointer", display:"flex", alignItems:"center", gap:4, marginBottom:12 }}>
          <input type="checkbox" checked={p.preserveLum}
            onChange={e => set("preserveLum", e.target.checked)}
            style={{ width:12, height:12, margin:0 }}/>
          Preserve Luminosity
        </label>
        <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
          <OptBtn onClick={() => setP(s => ({ ...DEFAULT_CB, preserveLum:s.preserveLum }))}>Reset</OptBtn>
          <OptBtn onClick={() => { if (onPreviewCancel) onPreviewCancel(); onClose(); }}>Cancel</OptBtn>
          <OptBtn accent onClick={() => { onCommit(p); onClose(); }}>Apply</OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── Image Size ───────────────────────────────────────────────────────────────

export function ImageSizeDialog({ open, currentW, currentH, onCommit, onClose }) {
  const [size, setSize] = useState({ w:currentW, h:currentH, lock:true, origW:currentW, origH:currentH });

  useEffect(() => {
    if (!open) return;
    setSize({ w:currentW, h:currentH, lock:true, origW:currentW, origH:currentH });
  }, [open, currentW, currentH]);

  if (!open) return null;

  const inputStyle = { width:"100%", background:V.panelActive, border:`1px solid ${V.panelBorder2}`,
    color:V.text, fontFamily:V.mono, fontSize:11, padding:"6px 8px", borderRadius:3, outline:"none" };

  return (
    <Modal onClose={onClose}>
      <div style={{ padding:"4px 0" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:16 }}>Image Size</div>
        <div style={{ fontSize:9, color:V.muted, marginBottom:8 }}>Current: {currentW} × {currentH} px</div>
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, color:V.muted, marginBottom:3 }}>Width</div>
            <input type="number" value={size.w} min={1} max={8192} style={inputStyle}
              onChange={e => {
                const nw = +e.target.value;
                setSize(s => ({ ...s, w:nw, h: s.lock && s.origW ? Math.round(nw/s.origW*s.origH) : s.h }));
              }}/>
          </div>
          <div onClick={() => setSize(s => ({ ...s, lock:!s.lock }))}
            style={{ cursor:"pointer", color:size.lock ? V.accentL : V.ghost, fontSize:14, marginTop:16 }}
            title="Lock aspect ratio">
            {size.lock ? "🔗" : "🔓"}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, color:V.muted, marginBottom:3 }}>Height</div>
            <input type="number" value={size.h} min={1} max={8192} style={inputStyle}
              onChange={e => {
                const nh = +e.target.value;
                setSize(s => ({ ...s, h:nh, w: s.lock && s.origH ? Math.round(nh/s.origH*s.origW) : s.w }));
              }}/>
          </div>
        </div>
        <div style={{ fontSize:9, color:V.ghost, marginBottom:12 }}>Resamples all layers with bicubic interpolation</div>
        <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
          <OptBtn onClick={onClose}>Cancel</OptBtn>
          <OptBtn accent onClick={() => { onCommit(size.w, size.h); onClose(); }}>Resize</OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── Canvas Size ──────────────────────────────────────────────────────────────

export function CanvasSizeDialog({ open, currentW, currentH, onCommit, onClose }) {
  const [size,   setSize]   = useState({ w:currentW, h:currentH });
  const [anchor, setAnchor] = useState("mc");

  useEffect(() => {
    if (!open) return;
    setSize({ w:currentW, h:currentH });
    setAnchor("mc");
  }, [open, currentW, currentH]);

  if (!open) return null;

  const inputStyle = { width:"100%", background:V.panelActive, border:`1px solid ${V.panelBorder2}`,
    color:V.text, fontFamily:V.mono, fontSize:11, padding:"6px 8px", borderRadius:3, outline:"none" };

  // Preview: where does existing content sit in the new canvas?
  const pw = 100;
  const ph = Math.round(100 * (size.h / size.w)) || 60;
  const ow = Math.round(pw * (currentW / size.w));
  const oh = Math.round(ph * (currentH / size.h));
  const col = anchor.length >= 2 ? anchor[1] : "c";
  const row = anchor[0];
  const ox = col==="l" ? 0 : col==="c" ? Math.round((pw-ow)/2) : pw-ow;
  const oy = row==="t" ? 0 : row==="m" ? Math.round((ph-oh)/2) : ph-oh;

  return (
    <Modal onClose={onClose}>
      <div style={{ padding:"4px 0" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:16 }}>Canvas Size</div>
        <div style={{ fontSize:9, color:V.muted, marginBottom:8 }}>Current: {currentW} × {currentH} px</div>
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, color:V.muted, marginBottom:3 }}>Width</div>
            <input type="number" value={size.w} min={1} max={8192} style={inputStyle}
              onChange={e => setSize(s => ({ ...s, w:+e.target.value }))}/>
          </div>
          <span style={{ color:V.ghost, marginTop:16 }}>×</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, color:V.muted, marginBottom:3 }}>Height</div>
            <input type="number" value={size.h} min={1} max={8192} style={inputStyle}
              onChange={e => setSize(s => ({ ...s, h:+e.target.value }))}/>
          </div>
        </div>

        {/* Anchor point selector */}
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:10, color:V.muted, marginBottom:6 }}>Anchor</div>
          <div style={{ display:"inline-grid", gridTemplateColumns:"repeat(3,24px)", gap:2,
            background:V.panelActive, padding:4, borderRadius:4, border:`1px solid ${V.panelBorder2}` }}>
            {["tl","tc","tr","ml","mc","mr","bl","bc","br"].map(a => (
              <div key={a} onClick={() => setAnchor(a)}
                style={{ width:24, height:24, borderRadius:3, cursor:"pointer", display:"flex",
                  alignItems:"center", justifyContent:"center",
                  background: anchor===a ? V.accent : "transparent",
                  border:    `1px solid ${anchor===a ? V.accentL : V.panelBorder2}`,
                  transition: "all 0.1s" }}
                onMouseEnter={e => { if (anchor!==a) e.currentTarget.style.background=V.panelHover; }}
                onMouseLeave={e => { if (anchor!==a) e.currentTarget.style.background="transparent"; }}>
                <div style={{ width:6, height:6, borderRadius:anchor===a?1:6,
                  background:anchor===a?"#fff":V.ghost, transition:"all 0.15s" }}/>
              </div>
            ))}
          </div>
          <span style={{ fontSize:9, color:V.ghost, marginLeft:10, verticalAlign:"top" }}>
            {ANCHORS[anchor] ?? "center"}
          </span>
        </div>

        {/* Preview */}
        <div style={{ marginBottom:12, display:"flex", justifyContent:"center" }}>
          <div style={{ position:"relative", width:pw, height:ph,
            background:"rgba(37,99,235,0.08)", border:`1px solid ${V.panelBorder2}`, borderRadius:3, overflow:"hidden" }}>
            <div style={{ position:"absolute", left:ox, top:oy,
              width:Math.max(2,ow), height:Math.max(2,oh),
              background:"rgba(37,99,235,0.25)", border:`1px solid ${V.accent}`, borderRadius:1 }}/>
          </div>
        </div>

        <div style={{ fontSize:9, color:V.ghost, marginBottom:4 }}>
          {size.w < currentW || size.h < currentH
            ? "⚠ Smaller canvas will crop — content may be lost"
            : "Larger canvas adds transparent area around the image"}
        </div>
        <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
          <OptBtn onClick={onClose}>Cancel</OptBtn>
          <OptBtn accent onClick={() => { onCommit(size.w, size.h, anchor); onClose(); }}>Apply</OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── About ─────────────────────────────────────────────────────────────────────

export function AboutDialog({ open, onClose }) {
  if (!open) return null;
  return (
    <Modal onClose={onClose}>
      <div style={{ textAlign:"center", padding:"24px 8px", maxWidth:340, margin:"0 auto", fontFamily:V.mono }}>
        <div style={{ fontFamily:V.display, fontSize:28, color:V.brand, marginBottom:8, letterSpacing:"-0.5px" }}>PixAlloy</div>
        <div style={{ fontSize:9, color:V.muted, letterSpacing:3, marginBottom:24, textTransform:"uppercase", fontWeight:400 }}>
          Photo Editor
        </div>
        <div style={{ fontSize:11, color:V.text, lineHeight:1.7, marginBottom:24, fontWeight:400 }}>
          Built on the <span style={{ color:V.brand, fontWeight:600 }}>Alloyhead</span> framework<br/>
          <span style={{ fontSize:10, color:V.muted, fontWeight:400 }}>by Pradeep Davuluri</span>
        </div>
        <div style={{ width:32, height:1, background:V.panelBorder2, margin:"0 auto 20px" }}/>
        <div style={{ fontSize:10, color:V.muted, marginBottom:12, fontWeight:600, letterSpacing:1, textTransform:"uppercase" }}>
          Special Thanks
        </div>
        <div style={{ fontSize:11, color:"#999", lineHeight:1.8, marginBottom:12, fontWeight:400 }}>
          To my family, for giving me the freedom<br/>to follow my curiosity, even when it<br/>didn't always make sense.
        </div>
        <div style={{ fontSize:11, color:"#999", lineHeight:1.8, marginBottom:20, fontWeight:400 }}>
          To my friends from Kendriya Vidyalaya Trimulgherry, who showed concern<br/>when I stumbled and pride when<br/>I found my footing.
        </div>
        <div style={{ fontSize:9, color:V.ghost, marginBottom:16, fontWeight:400 }}>v0.8</div>
        <div onClick={onClose}
          style={{ display:"inline-block", padding:"6px 24px", borderRadius:4,
            border:`1px solid ${V.panelBorder2}`, color:V.muted, cursor:"pointer",
            fontSize:10, fontWeight:500, transition:"all 0.15s" }}>
          Close
        </div>
      </div>
    </Modal>
  );
}

// ── Group Delete Confirmation ─────────────────────────────────────────────────

export function GroupDeleteDialog({ confirm, onRelease, onDeleteAll, onClose }) {
  if (!confirm) return null;
  return (
    <Modal onClose={onClose}>
      <div style={{ textAlign:"center", padding:"12px 0" }}>
        <div style={{ fontSize:16, marginBottom:8 }}>📁</div>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:8 }}>
          Delete Group "{confirm.name}"
        </div>
        <div style={{ fontSize:11, color:V.muted, marginBottom:20, lineHeight:1.6 }}>
          This group contains {confirm.childCount} layer{confirm.childCount>1?"s":""}.<br/>
          What would you like to do with them?
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
          <OptBtn onClick={onRelease}>Release Layers</OptBtn>
          <OptBtn accent onClick={onDeleteAll}>Delete All</OptBtn>
          <OptBtn onClick={onClose}>Cancel</OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── Save Guard ────────────────────────────────────────────────────────────────

export function SaveGuardDialog({ guard, onExportAndContinue, onDiscard, onClose }) {
  if (!guard) return null;
  return (
    <Modal onClose={onClose}>
      <div style={{ textAlign:"center", padding:"12px 0" }}>
        <div style={{ fontSize:16, marginBottom:8 }}>⚠</div>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:8 }}>Unsaved Changes</div>
        <div style={{ fontSize:11, color:V.muted, marginBottom:20, lineHeight:1.6 }}>
          You have unsaved changes that will be lost.<br/>Would you like to export first?
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
          <OptBtn onClick={onExportAndContinue}>Export &amp; Continue</OptBtn>
          <OptBtn accent onClick={onDiscard}>Discard Changes</OptBtn>
          <OptBtn onClick={onClose}>Cancel</OptBtn>
        </div>
      </div>
    </Modal>
  );
}
